import React, { FC, useEffect, useRef, useState } from 'react'
import styles from './ReviewSlider.module.scss'
import Plyr from 'plyr'
import PlayIcon from './play.svg'
import UnmutedIcon from './muted.svg'
import MutedIcon from './unmuted.svg'
import cn from 'classnames'
import Loader from '../../../common/Loader/Loader'
import Image from '../../../common/Image/Image'
import { getImageSrc } from '../../../../lib/utils'

const VideoSlide: FC<{
  src: string
  cover: string
  active: boolean
  index: number
}> = ({ src, active, index, cover }) => {
  const [plyrInstance, setPlyrInstance] = useState<Plyr>(null)
  const [play, setPlay] = useState(false)
  const [muted, setMuted] = useState(true)
  const [isHorizon, setIsHorizon] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const plyrOptions: Plyr.Options = {
    muted,
    controls: ['progress'],
    fullscreen: {
      enabled: false,
    },
    hideControls: false,
  }

  useEffect(() => {
    if (videoRef) {
      const plyr = new Plyr(videoRef.current, plyrOptions)
      const onPlay = () => setPlay(true)
      const onPause = () => setPlay(false)

      plyr.on('pause', onPause)
      plyr.on('play', onPlay)

      setPlyrInstance(plyr)

      const handleLoadedMetadata = () => {
        const videoElement = videoRef.current

        setIsHorizon(videoElement.videoWidth >= videoElement.videoHeight)
        setIsLoading(false)
      }

      const videoElement = videoRef.current
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
        plyr.off('pause', onPause)
        plyr.off('play', onPlay)
      }
    }
  }, [])

  useEffect(() => {
    if (plyrInstance) {
      plyrInstance.muted = muted
    }
  }, [muted, plyrInstance])

  useEffect(() => {
    if (!plyrInstance) return
    if (!active) {
      plyrInstance.pause()
      plyrInstance.currentTime = 0
    }
  }, [active])

  return (
    <>
      <div className={styles.videoWrapper}>
        <div
          className={cn(
            styles.videoContainer,
            isHorizon
              ? styles['videoContainer--horizon']
              : styles['videoContainer--vertical'],
          )}
        >
          <video
            poster={`${
              process.env.NEXT_PUBLIC_IMAGE_OPTIMIZER
            }/rs:fill/w:670/q:75/plain/${getImageSrc(cover)}`}
            playsInline
            ref={videoRef}
            src={src}
            className={cn(
              styles.video,
              isHorizon ? styles['video--horizon'] : styles['video--vertical'],
              isLoading && styles['video--loading'],
            )}
            onClick={() => {
              play ? plyrInstance.pause() : plyrInstance.play()
            }}
          />
          {isLoading ? (
            <Loader className={styles.loader} />
          ) : (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMuted((prev) => !prev)
                }}
                type="button"
                className={styles.mute}
                aria-label={muted ? 'Включить звук' : 'Выключить звук'}
              >
                {muted ? <MutedIcon /> : <UnmutedIcon />}
              </button>
              {!play && (
                <button
                  type="button"
                  className={styles.play}
                  onClick={() => {
                    plyrInstance.play()
                    setPlay(true)
                  }}
                  aria-label="Включить видео"
                >
                  <PlayIcon />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles.bg}>
        <Image
          src={cover}
          alt={`слайд - ${index + 1}`}
          width={670}
          height={800}
          loading={index === 0 ? 'eager' : 'lazy'}
          priority={index === 0}
        />
      </div>
    </>
  )
}

export default VideoSlide
